import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { FC, useEffect, useState } from 'react';
import { Route } from 'react-router';
import { CareMapState } from '../caremap/CareMapState';
import { ActiveCareMapProviderV2 } from '../ActiveCareMapProvider';
import { InnerhivePage } from '../InnerhivePage';
import { CareMapNotesList } from './note-list/CareMapNotesList';
import { IonButtons, IonToolbar } from '@ionic/react';
import { FullWidthHeader } from '../components/FullWidthHeader';
import { AddNoteButton } from './add-note/AddNoteButton';
import { ViewNoteWithQueryParamDrawer } from './view-note/ViewNoteWithQueryParamDrawer';
import { CaremapNotesModelFactory } from './model/CaremapNotesModelFactory';
import { AddNoteWithQueryParamDrawer } from './add-note/AddNoteWithQueryParamDrawer';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { CareTabsProvider } from '../care/CareTabsProvider';
import { CareRecipientSwitcher } from '../navigation/CareRecipientSwitcher';
import { AddCareMapButton } from '../caremap/AddCareMapButton';
import { NotesSortControl, SortField, SortOrder } from './components/NotesSortControl';
import { NotesFilterControl, FilterState } from './components/NotesFilterControl';
import { RecordingState } from '../appointments/recording/RecordingState';
import { OfflineRecordingListItem } from '../appointments/recording/OfflineRecordingListItem';
import { sortByNewest } from '../utils/arrays';
import { AppPrincipal, isAnonymousViewer } from '../auth/AppPrincipal';
import { useBreakpoints } from '../theme/useBreakpoints';

export const NotesPageRouteHandler: React.FC = () => (
    <Route exact path="/notes/:caremapId" render={routeParams => (
        <CareMapNotesPage careMapId={routeParams.match.params.caremapId} />
    )} />
);

export const CareMapNotesPage: FC<{ careMapId: string; }> = ({ careMapId }) => {
    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    return (
        <ActiveCareMapProviderV2 careMapId={careMapId}>
            <ActiveCaremapNotesPage />
        </ActiveCareMapProviderV2>
    );
};

export const ActiveCaremapNotesPage: React.FC = () => {
    const mixpanel = useInstance(MixPanelEventEmitter);

    const viewNoteDrawer = useInstance(ViewNoteWithQueryParamDrawer);
    const addNoteDrawer = useInstance(AddNoteWithQueryParamDrawer);

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const notesModelFactory = useInstance(CaremapNotesModelFactory);
    useSubscription(() => notesModelFactory);

    const recordingState = useInstance(RecordingState);
    const recordingStateData = useSubscription(() => recordingState);
    const offlineRecordings = recordingStateData?.offlineRecordings || [];

    const tabsProvider = useInstance(CareTabsProvider);
    const appPrincipal = useInstance(AppPrincipal);

    const [sortField, setSortField] = useState<SortField>('updatedAt');
    const [sortOrder, setSortOrder] = useState<SortOrder>('newest');
    const [filters, setFilters] = useState<FilterState>({
        noteType: [],
        careCategory: [],
        tags: []
    });

    const { isBelowBreakpoint } = useBreakpoints();
    const isMobile = isBelowBreakpoint('md');

    const activeCaremap = careMapState.getActiveCaremap();

    useEffect(() => {
        mixpanel.track('Notes Page Opened');
    }, [mixpanel]);


    if (!activeCaremap){
        return null;
    }

    const incompleteCareMap = careMapState.getIncompleteCareMap();

    const allNotes = notesModelFactory.forActiveCaremap().getNotes();

    // Sort offline recordings by creation date (newest first)
    const sortedOfflineRecordings = sortByNewest(offlineRecordings, 'created');

    // Filter notes based on active filters
    const filterNotes = (notesToFilter: any[]) => {
        return notesToFilter.filter(note => {
            // Note Type filters (AND logic - note must have ALL selected types)
            if (filters.noteType.length > 0) {
                const hasRecording = Boolean(note.otherAttributes?.recording);
                const hasActionItems = Boolean(note.otherAttributes?.recording?.summary?.actionItems?.length > 0);
                const hasOtherAttachments = Boolean(note.attachments?.length > 0) && (!hasRecording || note.attachments?.length > 1);
                const matchesAllNoteTypes = filters.noteType.every(filterType => {
                    switch (filterType) {
                        case 'hasRecording': return hasRecording;
                        case 'hasActionItems': return hasActionItems;
                        case 'hasOtherAttachments': return hasOtherAttachments;
                        default: return false;
                    }    });
                if (!matchesAllNoteTypes) return false;
            }
            // Care Category filters
            if (filters.careCategory.length > 0) {
                const noteSection = note.otherAttributes?.noteTarget?.section;
                if (!filters.careCategory.includes(noteSection)) return false;
            }

            // Tags filters (OR logic - note must have at least one selected tag)
            if (filters.tags.length > 0) {
                const noteTags = note.tags || [];
                const hasMatchingTag = filters.tags.some(filterTag => noteTags.includes(filterTag));
                if (!hasMatchingTag) return false;
            }

            return true;
        });
    };
    const notes = filterNotes(allNotes);

    // Check if any filters are active
    const hasActiveFilters = filters.noteType.length > 0 || filters.careCategory.length > 0;

    const canEdit = activeCaremap.links.self.actions.includes('edit-notes');

    const editNote = (noteId: string) => {
        viewNoteDrawer.open({ noteId });
    };

    const addNote = () => {
        addNoteDrawer.open();
    };

    const canAddNotes = careMapState.hasPermissionTo('post-note');

    // NOTE: heightOffset accounts for the fixed header containing switcher,
    // which is taller for anonymous due to expiration message
    const isAnonymous = isAnonymousViewer(appPrincipal);
    const heightOffset = 80 + (isAnonymous ? 28 : 0);

    return (
        <>
            <InnerhivePage title="Care" tabs={tabsProvider.getTabs()}>
                <FullWidthHeader fixed>
                    <IonToolbar>
                        {incompleteCareMap ? (
                            <AddCareMapButton />
                        ): (
                            <>
                                <div style={{margin: '4px 12px 0 4px', paddingTop: 5, paddingBottom: 10}}>
                                    <CareRecipientSwitcher />
                                </div>
                                <IonButtons slot="end">
                                    {allNotes.length > 0 && (
                                        <>
                                            <NotesSortControl
                                                sortField={sortField}
                                                sortOrder={sortOrder}
                                                onSortFieldChange={setSortField}
                                                onSortOrderChange={setSortOrder}
                                                triggerId="sort-trigger-toolbar"
                                                iconOnly={isMobile}
                                            />
                                            <NotesFilterControl
                                                filters={filters}
                                                onFiltersChange={setFilters}
                                                notes={allNotes}
                                                triggerId="filter-trigger-toolbar"
                                                iconOnly={isMobile}
                                            />
                                        </>
                                    )}
                                    {canAddNotes && (
                                        <AddNoteButton
                                            useIcon
                                            canAdd={canEdit}
                                            onClick={addNote}
                                            iconOnly={isMobile}
                                        />
                                    )}
                                </IonButtons>

                            </>
                        )}
                    </IonToolbar>
                </FullWidthHeader>

                {notesModelFactory.forActiveCaremap().hasLoaded && (
                    <>
                        {/* Show offline recordings section */}
                        {sortedOfflineRecordings.length > 0 && (
                            <div style={{ marginBottom: '24px' }} data-id="offline-recordings-list">
                                {sortedOfflineRecordings.map((recording) => (
                                    <OfflineRecordingListItem
                                        key={recording.id}
                                        recording={recording}
                                        showBottomLine={true}
                                    />
                                ))}
                            </div>
                        )}

                        {notes.length || canEdit ? (
                            <CareMapNotesList
                                notes={notes}
                                showHeading={false}
                                showNoteTargetHeader
                                onNoteClicked={editNote}
                                onAddNote={addNote}
                                showSortOptions={false}
                                sortField={sortField}
                                sortOrder={sortOrder}
                                hasActiveFilters={hasActiveFilters}
                                heightOffset={heightOffset}
                            />
                        ) : (
                            <div data-type="viewer-no-notes">
                                No public notes yet… Check back soon!
                            </div>
                        )}
                    </>
                )}

            </InnerhivePage>
        </>
    );
};
